import * as React from "react";
import {none, option, Option} from "ts-option";
import {connect, Dispatch} from "react-redux";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import Job from "../../../model/job/Job";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import Loading from "../../ui/loading/Loading";
import Routes from "../../../navigation/route/Route";
import {fetchJobByRefNumber, fetchJobEvents} from "../../../store/action/job/job";
import {canDisplayJob, getJobByIdOrRefNumber} from "../../../store/view/job";
import {RootState} from "../../../store/reducer/root/RootState";
import JobStatusSelector from "../statusSelector/JobStatusSelector";
import {fetchInvoiceNotes, fetchJobNotes, fetchNotes} from "../../../store/action/note/notes";
import JobActivityTabs, {JobTab} from "../activityTabs/JobActivityTabs";
import Note from "../../../model/note/Note";
import JobActivityStream from "../activityStream/JobActivityStream";
import JobEvent from "../../../model/job/event/JobEvent";
import JobAppointment from "../../../model/appointment/job/JobAppointment";
import Toast from "../../helper/Toast";
import LeadEvent from "../../../model/lead/event/LeadEvent";
import {fetchLeadEvents} from "../../../store/action/lead/lead";
import IndividualContact from "../../../model/contact/IndividualContact";
import {getIndividualContact} from "../../../store/view/contact";
import {isDefined, notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {fetchMerchants} from "../../../store/action/merchant/merchant";
import {fetchInvoice, fetchInvoiceEvents, fetchInvoicePayments} from "../../../store/action/invoice/invoice";
import {getInvoiceByJobId} from "../../../store/view/invoice";
import {ImportantNotes} from "../../importantNotes/ImportantNotes";
import PartnerBrand, {IPartnerBrandPaymentsStatus} from "../../../model/partnerBrand/PartnerBrand";
import {getPartnerBrand} from "../../../store/view/partnerBrand";
import TextUtils from "../../../utils/TextUtils";
import PartnerBrandAssignee from "../../partnerBrand/PartnerBrandAssignee";
import JobAgentSelector from "../agentSelector/JobAgentSelector";
import Bill from "../../../model/bill/Bill";
import {fetchBillByJobId} from "../../../store/action/bill/bill";
import ButtonWithDropdown, {ButtonDropdownItem} from "../../../hiberUiKit/src/atoms/Buttons/ButtonWithDropdown";
import JobPinnedNotes from "../pinnedNotes/JobPinnedNotes";
import InvoiceEvent from "../../../model/invoice/event/InvoiceEvent";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import Payment from "../../../model/payment/Payment";
import JobSidebar from "../jobSidebar/JobSidebar";
import ActivityStreamUtils from "../../../utils/ActivityStreamUtils";
import PartnerBrandOutstandingBillsWarningModal from "../partnerBrandOutstandingBills/PartnerBrandOutstandingBillsWarningModal";
import {fetchBillOfMaterialsByJobId} from "../../../store/action/billOfMaterials/billOfMaterials";
import {fetchJobAppointments} from "../../../store/action/appointment/appointment";
import Settings from "../../../model/settings/Settings";
import DeepLinkLinks from "../../deeplink/DeepLinkLinks";
import {Bom} from "../../../model/job/Bom";
import {AuthenticatedEntityTypeEnum} from "../../../model/user/AuthenticatedEntity";
import {getUser} from "../../../store/view/user";
import classNames = require("classnames");

/**
 * Indicates a phone's screen; leadDetails.scss uses the same width.
 */
const PHONE_QUERY = "(max-width: 640px)";

/**
 * What a phone shows of the job: the activity or the details otherwise beside it.
 */
type PhoneTab = "activity" | "details";

interface RouteProps {
	/**
	 * Job reference number.
	 */
	jobReferenceNumber: string
}

type ComponentProps = RouteComponentProps<RouteProps>;

interface ReduxProps {
	/**
	 * Job data.
	 */
	job: Option<Job>;
	/**
	 * Job notes.
	 */
	notes: Note[];
	/**
	 * Lead notes.
	 */
	leadNotes: Note[];
	/**
	 * Invoice notes.
	 */
	invoiceNotes: AccountingNote[];
	/**
	 * Job events.
	 */
	jobEvents: Array<JobEvent>;
	/**
	 * Lead events.
	 */
	leadEvents: LeadEvent[];
	/**
	 * Invoice events.
	 */
	invoiceEvents: InvoiceEvent[];
	/**
	 * Invoice payments.
	 */
	invoicePayments: Payment[];
	/**
	 * Job appointments.
	 */
	appointments: JobAppointment[];
	/**
	 * Contact
	 */
	contact: Option<IndividualContact>
	/**
	 * Job
	 */
	jobs: Option<Job[]>
	/**
	 * Can display job
	 */
	canDisplayJob: boolean
	/**
	 * Partner brand
	 */
	partnerBrand: Option<PartnerBrand>
	/**
	 * Invoice identifier.
	 */
	invoiceId: Option<string>;
	/**
	 * Data of bill related to job.
	 */
	bill: Option<Bill>
	/**
	 * Partner brand payments status
	 */
	partnerBrandPaymentsStatus: Option<IPartnerBrandPaymentsStatus>
	/**
	 * Bill of materials
	 */
	bom: Option<Bom>
	/**
	 * Configuration
	 */
	config: Option<Settings>
	/**
	 * Name of the job's type.
	 */
	jobTypeName: Option<string>
	/**
	 * Name of the user who created the job.
	 */
	creatorName: Option<string>
}

interface DispatchProps {
	/**
	 * Fetch job
	 * @param {string} jobRefNumber
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch job history
	 * @param {string} jobId
	 */
	fetchJobHistory: (jobId: string) => any;
	/**
	 * Fetch job notes
	 * @param {string} jobId
	 */
	fetchJobNotes: (jobId: string) => any;
	/**
	 * Fetch notes action.
	 * @param {string} leadId lead identifier.
	 */
	fetchNotes: (leadId: string) => void;
	/**
	 * Fetch invoice notes action.
	 * @param {string} invoiceId invoice identifier.
	 */
	fetchInvoiceNotes: (invoiceId: string) => void;
	/**
	 * Fetch lead history.
	 * @param {string} leadId lead identifier.
	 */
	fetchLeadHistory: (leadId: string) => void;
	/**
	 * Fetch invoice history.
	 * @param {string} invoiceId invoice identifier.
	 */
	fetchInvoiceHistory: (invoiceId: string) => void;
	/**
	 * Fetch invoice payments.
	 * @param {string} invoiceId invoice identifier.
	 */
	fetchInvoicePayments: (invoiceId: string) => void;
	/**
	 * Fetch merchants
	 */
	fetchMerchants: () => void
	/**
	 * Fetch invoice action.
	 * @param {string} jobId job identifier.
	 */
	fetchInvoice: (jobId: string) => void;
	/**
	 * Fetch bill by job id action.
	 */
	fetchBill: (jobId: string) => void;
	/**
	 * Fetch bill of materials
	 */
	fetchBillOfMaterialsByJobId: (jobId: string) => void
	/**
	 * Fetch job appointments
	 * @param jobId
	 */
	fetchAppointments: (jobId: string) => void
}

interface RouteProps {
	jobId: string;
}

interface State {
	/**
	 * Note to edit.
	 */
	noteToEdit: Option<Note>;
	/**
	 * JobAppointment to edit.
	 */
	appointmentToEdit: Option<JobAppointment>;
	/**
	 * Selected tab id.
	 */
	selectedTabId: JobTab;
	/**
	 * Job fetching in progress
	 */
	jobFetching: boolean;
	/**
	 * Job fetched
	 */
	jobFetched: boolean;
	/**
	 * Payments outstanding modal visible flag
	 */
	paymentsOutstandingModalVisible: boolean
	/**
	 * Indicates a phone's screen.
	 */
	isPhone: boolean
	/**
	 * Tab shown on a phone.
	 */
	phoneTab: PhoneTab
	/**
	 * Indicates if the activity filters are shown on a phone.
	 */
	streamFiltersOpen: boolean
}

type Props = ComponentProps & ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

class JobDetails extends React.PureComponent<Props, State> {
	private phoneQuery: MediaQueryList = window.matchMedia ? window.matchMedia(PHONE_QUERY) : null;

	constructor(props: Props) {
		super(props);
		this.state =
			{
				noteToEdit: none,
				selectedTabId: JobTab.Note,
				jobFetched: false,
				jobFetching: false,
				appointmentToEdit: none,
				paymentsOutstandingModalVisible: false,
				isPhone: !!this.phoneQuery && this.phoneQuery.matches,
				phoneTab: "activity",
				streamFiltersOpen: false
			};
		this.navigateToJobsList = this.navigateToJobsList.bind(this);
		this.checkJobExists = this.checkJobExists.bind(this);
		this.onEditNoteClick = this.onEditNoteClick.bind(this);
		this.onNoteEditSuccess = this.onNoteEditSuccess.bind(this);
		this.onTabsSwitch = this.onTabsSwitch.bind(this);
		this.onEditAppointmentClick = this.onEditAppointmentClick.bind(this);
		this.onAppointmentEditSuccess = this.onAppointmentEditSuccess.bind(this);
		this.fetchLeadData = this.fetchLeadData.bind(this);
		this.checkInvoiceExists = this.checkInvoiceExists.bind(this);
		this.checkBillExists = this.checkBillExists.bind(this);
	}

	componentDidMount() {
		this.phoneQuery && this.phoneQuery.addListener(this.onPhoneQueryChange);
		this.props.fetchMerchants();
		this.props.job.map(j => {
			this.props.fetchJobHistory(j.id);
			this.props.fetchJobNotes(j.id);
			j.leadId.map(id => this.fetchLeadData(id));
			this.checkInvoiceExists(j.id);
			this.checkBillExists(j.id);
			this.checkBOMExists(j.id);
			this.props.fetchAppointments(j.id);
		});
		this.checkJobExists();
		this.props.job.map(j => !j.data.partsOnly).getOrElse(false) && this.props.invoiceId.map(this.fetchInvoiceData);
	}

	componentWillReceiveProps(nextProps: Props) {
		if ((this.props.job.isEmpty && nextProps.job.isDefined) ||
			(this.props.job.isDefined && nextProps.job.isDefined && this.props.job.get.id != nextProps.job.get.id)) {
			nextProps.job.map(j => {
				this.props.fetchJobNotes(j.id);
				this.props.fetchJobHistory(j.id);
				j.leadId.map(id => this.fetchLeadData(id));
				this.checkInvoiceExists(j.id);
				this.checkBillExists(j.id, nextProps);
				this.checkBOMExists(j.id, nextProps);
				this.props.fetchAppointments(j.id);
			})
		}
		if (this.props.job.map(j => j.id).orNull != nextProps.job.map(j => j.id).orNull) {
			this.setState({jobFetched: false, jobFetching: false}, this.checkJobExists);
		}

		if (this.props.invoiceId.isEmpty
			&& nextProps.invoiceId.isDefined
			&& nextProps.job.map(j => !j.data.partsOnly).getOrElse(false)) {
			nextProps.invoiceId.map(this.fetchInvoiceData);
		}
	}

	componentWillUnmount() {
		this.phoneQuery && this.phoneQuery.removeListener(this.onPhoneQueryChange);
	}

	onPhoneQueryChange = (query: MediaQueryListEvent) => this.setState({isPhone: query.matches});

	fetchLeadData(leadId: string) {
		this.props.fetchLeadHistory(leadId);
		this.props.fetchNotes(leadId);
	}

	fetchInvoiceData = (invoiceId: string) => {
		this.props.fetchInvoiceHistory(invoiceId);
		this.props.fetchInvoiceNotes(invoiceId);
		this.props.fetchInvoicePayments(invoiceId);
	};

	render() {
		const bdpId = this.props.partnerBrand.flatMap(pb => pb.bdpId);
		const accountManagerId = this.props.partnerBrand.flatMap(pb => pb.accountManagerId);
		const {job} = this.props;
		const {isPhone, phoneTab} = this.state;
		const isPartsOnly: boolean = job.map(j => j.data.partsOnly).getOrElse(false);
		return (
			<>
				{isPartsOnly && <div className="l-job-details-parts-only-indicator">
					Parts only job
				</div>}
				<div className={"l-page boxed"}>
					<Loading isLoading={this.props.job.isEmpty || !this.props.canDisplayJob}>
						{
							job.map(j => {
								if (isPhone) {
									return this.renderPhoneHeader(j);
								}
								return (
									<div className={"l-page_header"}>
										<div className="l-lead-details-header">
											<div className={"l-lead-details-header_reference-number-and-button"}>
												<div>
													<div className={"t-module-heading"}>Job reference</div>
													<div title={"Copy job reference"}
														 className={"t-hero l-reference-number"}
														 onClick={this.copyReferenceNumberToClipboard}>
														{j.referenceNumber}
													</div>
												</div>
												<DeepLinkLinks onUniversalDeeplinkClick={this.copyUniversalLinkToClipboard}
															   onAppDeeplinkClick={this.copyAppLinkToClipboard}/>
											</div>
											<div className={"l-lead-details-header_activity"}>
												{this.renderShowButton(j)}
												<JobAgentSelector job={j}/>
												<PartnerBrandAssignee title="AM" userId={accountManagerId}/>
												<PartnerBrandAssignee title="BDP" userId={bdpId}/>
												<JobStatusSelector job={j}/>
											</div>
										</div>
									</div>
								)
							}).orNull
						}
						{/* A phone shows either the activity or the details, both kept mounted so a note being written
						    or the details fetched survive a switch. */}
						<div className={classNames({"is-phone-hidden": isPhone && phoneTab !== "activity"})}>
							<ImportantNotes partnerBrand={this.props.partnerBrand}/>
							<JobPinnedNotes
								notes={this.props.notes.filter(note => note.pinningData.pinned && !note.isReminder()).sort(
									ActivityStreamUtils.sort)}
								jobId={job.map(j => j.id)}/>
						</div>
						<div className={"l-page_content l-lead-details"}>
							<div className={classNames("l-lead-details_info",
								{"is-phone-hidden": isPhone && phoneTab !== "details"})}>
								{isPhone && job.map(j =>
									<div className={"c-job-phone-assignees"}>
										<JobAgentSelector job={j}/>
										<PartnerBrandAssignee title="AM" userId={accountManagerId}/>
										<PartnerBrandAssignee title="BDP" userId={bdpId}/>
									</div>).orNull}
								<JobSidebar job={job} contact={this.props.contact}/>
							</div>
							<div className={classNames("l-lead-details_activity",
								{"is-phone-hidden": isPhone && phoneTab !== "activity"})}>
								<JobActivityTabs job={this.props.job}
												 noteToEdit={this.state.noteToEdit}
												 onNoteEditSuccess={this.onNoteEditSuccess}
												 selectedTabId={this.state.selectedTabId}
												 appointmentToEdit={this.state.appointmentToEdit}
												 onAppointmentEditSuccess={this.onAppointmentEditSuccess}
												 onTabsSwitch={this.onTabsSwitch}
												 partnerBrandId={this.props.contact.flatMap(c => c.data.partnerBrandId)}
												 brandId={this.props.contact.map(c => c.data.brandId)}/>
								{isPhone &&
								<div className={"c-job-phone-stream-heading"}>
									<span>All activity</span>
									<button type={"button"} className={"t-link"}
											onClick={() => this.setState({streamFiltersOpen: !this.state.streamFiltersOpen})}>
										{this.state.streamFiltersOpen ? "Hide filters" : "Filter"}
									</button>
								</div>}
								<div className={classNames("l-lead-details_activity_stream",
									{"is-filters-closed": isPhone && !this.state.streamFiltersOpen})}>
									<JobActivityStream notes={this.props.notes}
													   leadNotes={this.props.leadNotes}
													   invoiceNotes={this.props.invoiceNotes}
													   jobEvents={this.props.jobEvents}
													   leadEvents={this.props.leadEvents}
													   invoiceEvents={this.props.invoiceEvents}
													   invoicePayments={this.props.invoicePayments}
													   activeNoteId={this.props.location.state ?
														   this.props.location.state.activeId : ""}
													   onEditNoteClick={this.onEditNoteClick}
													   onEditAppointmentClick={this.onEditAppointmentClick}
													   onPartsOnlyStatusChange={this.onPartsOnlyStatusChange}
													   job={this.props.job}
													   bill={this.props.bill}/>
								</div>
							</div>
						</div>
					</Loading>
				</div>
				{this.state.paymentsOutstandingModalVisible && <PartnerBrandOutstandingBillsWarningModal
					closeModal={() => this.setState({paymentsOutstandingModalVisible: false})}
					navigateToBill={this.navigateToBillOfMaterial}/>}
			</>
		);
	}

	/**
	 * A phone's header: the job's type, reference, partner brand and creator, its status, and the tabs that switch
	 * between the activity and the details.
	 */
	renderPhoneHeader(job: Job) {
		const subtitle = [this.props.partnerBrand.map(pb => pb.name).orNull, this.props.creatorName.orNull]
			.filter(part => !!part)
			.join(" · ");
		const tab = (id: PhoneTab, label: string) =>
			<button type={"button"}
					className={classNames("c-job-phone-header_tab", {"is-active": this.state.phoneTab === id})}
					onClick={() => this.setState({phoneTab: id})}>
				{label}
			</button>;
		return (
			<div className={"c-job-phone-header"}>
				<div className={"c-job-phone-header_top"}>
					<span className={"c-job-phone-header_type"}>{this.props.jobTypeName.getOrElse("")}</span>
					{this.renderShowButton(job)}
				</div>
				<div title={"Copy job reference"}
					 className={"c-job-phone-header_reference l-reference-number"}
					 onClick={this.copyReferenceNumberToClipboard}>
					{job.referenceNumber}
				</div>
				{subtitle && <div className={"c-job-phone-header_subtitle"}>{subtitle}</div>}
				<div className={"c-job-phone-header_status"}>
					<JobStatusSelector job={job} searchable={false}/>
				</div>
				<div className={"c-job-phone-header_tabs"}>
					{tab("activity", "Activity")}
					{tab("details", "Details")}
				</div>
			</div>
		);
	}

	renderShowButton(job: Job) {
		const isPBrandPaymentsStatusEnabled = this.props.partnerBrand.isDefined && this.props.partnerBrandPaymentsStatus.isEmpty;
		let dropdownItems: ButtonDropdownItem[] = [];
		if (job.data.allowBom && job.quoteId.orNull) {
			dropdownItems.push(new ButtonDropdownItem(ShowAction.MATERIALS, 'Bill of materials', isPBrandPaymentsStatusEnabled,
				isPBrandPaymentsStatusEnabled));
		}
		if (!job.data.partsOnly && this.props.invoiceId.isDefined) {
			dropdownItems.unshift(new ButtonDropdownItem(ShowAction.INVOICE, 'Invoice', false, false));
		}
		else if (job.data.partsOnly && this.props.bill.isDefined) {
			dropdownItems.unshift(new ButtonDropdownItem(ShowAction.BILL, 'Parts Bill', false, false));
		}
		return (
			dropdownItems.length > 0 &&
			<ButtonWithDropdown label={"Show"}
								dropdownItems={dropdownItems}
								onDropdownItemClick={this.onShowSelectClick}/>
		);
	}

	onShowSelectClick = (showActionId: string) => {
		if (showActionId === ShowAction.INVOICE) {
			this.props.job.map(j => this.props.history.push(Routes.InvoiceDetailsView(j.referenceNumber)));
		}
		else if (showActionId === ShowAction.BILL) {
			this.props.bill.map(b => this.props.history.push(Routes.BillDetailsView(b.id)));
		}
		else if (showActionId === ShowAction.MATERIALS) {
			if (this.props.partnerBrandPaymentsStatus.map(ps => ps.outstandingBills).getOrElse(false)) {
				this.setState({paymentsOutstandingModalVisible: true})
			}
			else {
				this.navigateToBillOfMaterial()
			}
		}
	};

	navigateToBillOfMaterial = () => this.props.job.map(j => this.props.history.push(Routes.BillOfMaterials(j.referenceNumber)));

	/**
	 * Check if invoice exists.
	 * @param {string} jobId
	 */
	checkInvoiceExists(jobId: string) {
		if (!this.props.invoiceId.isDefined) {
			this.props.fetchInvoice(jobId);
		}
	}

	onPartsOnlyStatusChange = (partsOnly: boolean) => {
		this.props.job.map(j => {
			if (partsOnly) {
				this.props.fetchBill(j.id);
			}
			else {
				this.props.fetchInvoice(j.id);
			}
		});
	};

	/**
	 * Check if bill exists.
	 * @param props
	 * @param {string} jobId
	 */
	checkBillExists(jobId: string, props: Props = this.props) {
		if (props.bill.isEmpty) {
			this.props.fetchBill(jobId);
		}
	}

	checkBOMExists(jobId: string, props: Props = this.props) {
		if (props.bom.isEmpty) {
			this.props.fetchBillOfMaterialsByJobId(jobId);
		}
	}

	/**
	 * If no job try fetch job again. If there is still no job, redirect to the jobs list
	 */
	checkJobExists() {
		if (this.state.jobFetched) {
			return;
		}
		else if (!this.state.jobFetching) {
			this.setState({jobFetching: true},
				() => this.props.fetchJobByRefNumber(
					this.props.match.params.jobReferenceNumber,
					() => {this.setState({jobFetched: true, jobFetching: false})},
					(err) => {
						notifyBugsnagAndShowToast(err, "Fetch job");
						this.navigateToJobsList()
					}
				)
			);
		}
	}


	/**
	 * Scroll page up to the edition fields.
	 */
	scrollToEdition() {
		if (window.scrollY > 100)
			window.scrollTo(0, 100);
	}

	/**
	 * Navigate to job list
	 */
	private navigateToJobsList() {
		this.props.history.push(Routes.JobsList);
	}

	/**
	 * On edit note click action.
	 * @param {Note} note note to edit.
	 */
	private onEditNoteClick(note: Note) {
		this.setState({
			noteToEdit: option(note),
			selectedTabId: note.reminderDate.isDefined ? JobTab.Reminder : JobTab.Note
		});
		this.scrollToEdition();
	}

	/**
	 * Clear note to edit on edit success.
	 */
	private onNoteEditSuccess() {
		this.setState({noteToEdit: none})
	}

	/**
	 * Activity tab switch callback.
	 * @param {JobTab} tabId
	 */
	private onTabsSwitch(tabId: JobTab) {
		this.setState({noteToEdit: none, appointmentToEdit: none, selectedTabId: tabId});
	}

	private onAppointmentEditSuccess() {
		this.setState({appointmentToEdit: none, selectedTabId: JobTab.Note})
	}

	/**
	 * On edit appointment click callback.
	 * @param {string} appointmentId
	 */
	private onEditAppointmentClick(appointmentId: string) {
		this.setState({
			appointmentToEdit: option(this.props.appointments.filter(a => a.id === appointmentId)[0]),
			selectedTabId: JobTab.Appointments
		});
		this.scrollToEdition();
	}

	private copyAppLinkToClipboard = () => {
		this.props.job.map(j => {
			TextUtils.copyToClipboard(`[Job details](partnerapp://job/${j.id})`);
			Toast.success("Mobile app deep-link has been copied to clipboard", "Copy link");
		});
	};

	private copyUniversalLinkToClipboard = () => {
		this.props.config.map(c =>
			this.props.job.map(j => {
				TextUtils.copyToClipboard(`${c.hiberWebsiteUrl}/deeplink/job/${j.id}`)
				Toast.success("Mobile universal deep-link has been copied to clipboard", "Copy link");
			}))
	};

	private copyReferenceNumberToClipboard = () => {
		this.props.job.map(j => {
			TextUtils.copyToClipboard(j.referenceNumber);
			Toast.success("Job reference has been copied to clipboard", "Copy job reference");
		});
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchJobByRefNumber(jobRefNumber, onSuccess, onFailure)),
		fetchJobHistory: (jobId: string) => dispatch(fetchJobEvents(
			jobId,
			() => {},
			() => Toast.error("Cannot fetch job events", "Job events")
		)),
		fetchJobNotes: (jobId: string) => dispatch(fetchJobNotes(
			jobId,
			() => {},
			() => Toast.error("Cannot fetch job notes", "Job notes")
		)),
		fetchNotes: (leadId: string) => dispatch(fetchNotes(leadId, () => {},
			() => Toast.error("Cannot fetch lead notes", "Lead notes"))),
		fetchInvoiceNotes: (invoiceId: string) => dispatch(fetchInvoiceNotes(invoiceId, () => {},
			() => Toast.error("Cannot fetch invoice notes", "Invoice notes"))),
		fetchLeadHistory: (leadId: string) => dispatch(fetchLeadEvents(leadId, () => {},
			() => Toast.error("Cannot fetch lead events", "Lead events"))),
		fetchInvoiceHistory: (invoiceId: string) => dispatch(fetchInvoiceEvents(invoiceId, () => {},
			() => Toast.error("Cannot fetch invoice events", "Invoice events"))),
		fetchInvoicePayments: (invoiceId: string) => dispatch(fetchInvoicePayments(invoiceId, () => {},
			() => Toast.error("Cannot fetch invoice payments", "Invoice payments"))),
		fetchMerchants: () => dispatch(fetchMerchants(() => {},
			(err) => notifyBugsnagAndShowToast(err, "Fetch merchants"))),
		fetchInvoice: (jobId: string) => dispatch(
			fetchInvoice(jobId, () => {}, () => {})
		),
		fetchBill: (jobId: string) => dispatch(fetchBillByJobId(jobId, () => {}, () => {})),
		fetchBillOfMaterialsByJobId: (jobId: string) => dispatch(fetchBillOfMaterialsByJobId(jobId, () => {}, () => {})),
		fetchAppointments: (jobId: string) => dispatch(
			fetchJobAppointments(jobId, () => {}, e => notifyBugsnagAndShowToast(e, "Fetch job appointments")))
	}
}

function mapStateToProps(state: RootState, props: ComponentProps) {
	const cdj = canDisplayJob(state);
	const job: Option<Job> = cdj ? getJobByIdOrRefNumber(props.match.params.jobReferenceNumber, state) : none;
	const jobId = job.map(j => j.id).orNull;
	const leadId = job.flatMap(j => j.leadId).orNull;
	const storeNotes = state.note.jobNotes[jobId];
	const notes: Note[] = !isDefined(storeNotes) ? [] : storeNotes;
	const storeLeadNotes = state.note.notes[leadId];
	const leadNotes: Note[] = !isDefined(storeLeadNotes) ? [] : storeLeadNotes;
	const jobEvents = state.job.events.getOrElse([]).filter(event => event.jobId === jobId);
	const leadEvents = state.lead.events.getOrElse([]).filter(event => event.leadId === leadId);
	const appointments = state.appointment.jobAppointments.getOrElse([]).filter(a => a.jobId.getOrElse("") === jobId);
	const contact = job.flatMap(j => getIndividualContact(j.data.principalContactId, state));
	const partnerBrandId = contact.flatMap(c => c.data.partnerBrandId);
	const partnerBrand = partnerBrandId.flatMap(pbId => getPartnerBrand(pbId, state));
	const invoiceId = getInvoiceByJobId(jobId, state).map(invoice => invoice.id);
	const invoiceNotes: AccountingNote[] = state.note.invoiceNotes[invoiceId.orNull] || [];
	const invoiceEvents = state.invoice.events[invoiceId.orNull] || [];
	const invoicePayments = option(state.invoice.payments[invoiceId.orNull]).getOrElse([]);
	const bill = option(state.bill.billsByJobIdMap[jobId]);
	const partnerBrandPaymentsStatus = option(state.partnerBrand.pBrandPaymentsStatus[partnerBrandId.orNull]);
	const bom = option(state.billOfMaterials.bomMap[jobId]);
	const config = state.settings.settings;
	const jobTypeName = job.flatMap(j => option(state.jobType.jobTypes.getOrElse([]).filter(jt => jt.id == j.data.jobTypeId)[0]))
		.map(jt => jt.name);
	const creatorName = job.map(j => j.creator)
		.flatMap(c => c.type == AuthenticatedEntityTypeEnum.User ? getUser(c.id) : none)
		.map(u => u.getDisplayName());
	return ({
		job,
		notes,
		leadNotes,
		invoiceNotes,
		jobEvents,
		leadEvents,
		invoiceEvents,
		invoicePayments,
		appointments,
		contact,
		canDisplayJob: cdj,
		partnerBrand,
		invoiceId,
		bill,
		partnerBrandPaymentsStatus,
		bom,
		config,
		jobTypeName,
		creatorName
	})
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(JobDetails));

enum ShowAction {
	INVOICE = 'INVOICE',
	BILL = "BILL",
	MATERIALS = 'MATERIALS'
}